import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useState, useEffect, useCallback } from 'react';
import { API } from '../../utils/config';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Calendar, 
  Clock, 
  User, 
  Home, 
  RefreshCw,
  CalendarClock,
  FileCheck,
  PenLine,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Send,
  MessageSquare,
  Camera,
  Check,
  Save
} from 'lucide-react';


const PassaggioConsegneDetail = () => {
  const { handoverId } = useParams();
  const navigate = useNavigate();
  const [handover, setHandover] = useState(null);
  const [loading, setLoading] = useState(true);
  const [proposedDate, setProposedDate] = useState('');
  const [proposedTime, setProposedTime] = useState('');
  const [message, setMessage] = useState('');
  const [verbaleTemplate, setVerbaleTemplate] = useState(null);
  const [verbale, setVerbale] = useState(null);
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState('info');
  const token = localStorage.getItem('hw_token');
  const currentUserEmail = JSON.parse(atob(token.split('.')[1])).email;

  const fetchHandover = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/api/handovers/${handoverId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setHandover(data);
        if (data.verbale) {
          setVerbale(data.verbale);
        }
      } else {
        toast.error('Passaggio consegne non trovato');
        navigate('/responsabile/passaggi-consegne');
      }
    } catch (error) {
      console.error('Error fetching handover:', error);
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  }, [handoverId, token, navigate]);

  const fetchVerbaleTemplate = useCallback(async () => {
    try {
      const response = await fetch(`${API}/api/handovers/template/verbale`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const template = await response.json();
        setVerbaleTemplate(template);
      }
    } catch (error) {
      console.error('Error fetching template:', error);
    }
  }, [token]);

  useEffect(() => {
    fetchHandover();
    fetchVerbaleTemplate();
  }, [fetchHandover, fetchVerbaleTemplate]);

  const proposeDate = async () => {
    if (!proposedDate || !proposedTime) {
      toast.error('Inserisci data e ora');
      return;
    }
    try {
      const response = await fetch(`${API}/api/handovers/${handoverId}/propose-date`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ date: proposedDate, time: proposedTime })
      });
      if (response.ok) {
        toast.success('Data proposta inviata');
        setProposedDate('');
        setProposedTime('');
        fetchHandover();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Errore');
      }
    } catch (error) {
      toast.error('Errore di rete');
    }
  };

  const confirmDate = async (index) => {
    try {
      const response = await fetch(`${API}/api/handovers/${handoverId}/confirm-date?date_index=${index}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success('Appuntamento confermato!');
        fetchHandover();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Errore');
      }
    } catch (error) {
      toast.error('Errore di rete');
    }
  };

  const startVerbale = async () => {
    try {
      const response = await fetch(`${API}/api/handovers/${handoverId}/start-verbale`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setVerbale(data.verbale);
        toast.success('Verbale iniziato');
        fetchHandover();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Errore');
      }
    } catch (error) {
      toast.error('Errore di rete');
    }
  };

  const saveVerbale = async (isComplete = false) => {
    setSaving(true);
    try {
      const response = await fetch(`${API}/api/handovers/${handoverId}/verbale`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ verbale, is_complete: isComplete })
      });
      if (response.ok) {
        toast.success(isComplete ? 'Verbale completato!' : 'Salvato');
        fetchHandover();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Errore');
      }
    } catch (error) {
      toast.error('Errore di rete');
    } finally {
      setSaving(false);
    }
  };

  const signHandover = async () => {
    try {
      const response = await fetch(`${API}/api/handovers/${handoverId}/sign`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        toast.success('Firmato con successo!');
        if (data.both_signed) {
          toast.success('Passaggio consegne completato!');
        }
        fetchHandover();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Errore');
      }
    } catch (error) {
      toast.error('Errore di rete');
    }
  };

  const sendMessage = async () => {
    if (!message.trim()) return;
    try {
      const response = await fetch(`${API}/api/handovers/${handoverId}/message?message=${encodeURIComponent(message)}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success('Messaggio inviato');
        setMessage('');
        fetchHandover();
      }
    } catch (error) {
      toast.error('Errore');
    }
  };

  const updateVerbaleField = (path, value) => {
    setVerbale(prev => {
      const newVerbale = { ...prev };
      const keys = path.split('.');
      let current = newVerbale;
      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) current[keys[i]] = {};
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
      return newVerbale;
    });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('it-IT', { 
      day: '2-digit', month: 'short', year: 'numeric' 
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('it-IT', { 
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const isOutgoing = handover?.outgoing_responsible?.email === currentUserEmail;
  const isIncoming = handover?.incoming_responsible?.email === currentUserEmail;
  const myRole = isOutgoing ? 'outgoing' : 'incoming';
  const hasSigned = myRole === 'outgoing' ? !!handover?.outgoing_signature : !!handover?.incoming_signature;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (!handover) return null;

  const getStatusConfig = (status) => {
    const configs = {
      pending_schedule: { label: 'Da fissare', color: 'bg-yellow-100 text-yellow-800' },
      scheduled: { label: 'Programmato', color: 'bg-blue-100 text-blue-800' },
      in_progress: { label: 'In corso', color: 'bg-purple-100 text-purple-800' },
      pending_signatures: { label: 'Firme', color: 'bg-orange-100 text-orange-800' },
      completed: { label: 'Completato', color: 'bg-green-100 text-green-800' },
      cancelled: { label: 'Annullato', color: 'bg-red-100 text-red-800' }
    };
    return configs[status] || configs.pending_schedule;
  };

  const statusConfig = getStatusConfig(handover.status);

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/responsabile/passaggi-consegne')}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">{handover.property_name}</h1>
          <p className="text-sm text-gray-500">{handover.property_address}</p>
        </div>
        <Badge className={`${statusConfig.color} px-4 py-2`}>{statusConfig.label}</Badge>
      </div>

      {/* Navigation Tabs */}
      <div className="flex gap-2 border-b pb-2">
        {[
          { id: 'info', label: 'Informazioni' },
          { id: 'schedule', label: 'Appuntamento' },
          { id: 'verbale', label: 'Verbale' },
          { id: 'messages', label: 'Messaggi' }
        ].map(tab => (
          <Button
            key={tab.id}
            variant={activeSection === tab.id ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setActiveSection(tab.id)}
          >
            {tab.label}
          </Button>
        ))}
      </div>

      {/* Info Section */}
      {activeSection === 'info' && (
        <div className="grid md:grid-cols-2 gap-6">
          {/* Property Info */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Home className="w-5 h-5" /> Immobile
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-sm text-gray-500">Nome</p>
                <p className="font-medium">{handover.property_name}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Indirizzo</p>
                <p className="font-medium">{handover.property_address || '-'}</p>
              </div>
              {handover.deadline && (
                <div>
                  <p className="text-sm text-gray-500">Scadenza</p>
                  <p className="font-medium text-red-600">{formatDate(handover.deadline)}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* People Info */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5" /> Responsabili
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-3 bg-red-50 rounded-lg">
                <p className="text-xs text-red-600 font-medium mb-1">USCENTE</p>
                <p className="font-medium">{handover.outgoing_responsible?.name}</p>
                <p className="text-sm text-gray-500">{handover.outgoing_responsible?.email}</p>
                {handover.outgoing_responsible?.phone && (
                  <p className="text-sm text-gray-500">{handover.outgoing_responsible?.phone}</p>
                )}
                {handover.outgoing_signature && (
                  <Badge className="mt-2 bg-green-100 text-green-800">
                    <Check className="w-3 h-3 mr-1" /> Firmato
                  </Badge>
                )}
              </div>
              <div className="p-3 bg-green-50 rounded-lg">
                <p className="text-xs text-green-600 font-medium mb-1">ENTRANTE</p>
                <p className="font-medium">{handover.incoming_responsible?.name}</p>
                <p className="text-sm text-gray-500">{handover.incoming_responsible?.email}</p>
                {handover.incoming_responsible?.phone && (
                  <p className="text-sm text-gray-500">{handover.incoming_responsible?.phone}</p>
                )}
                {handover.incoming_signature && (
                  <Badge className="mt-2 bg-green-100 text-green-800">
                    <Check className="w-3 h-3 mr-1" /> Firmato
                  </Badge>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Schedule Section */}
      {activeSection === 'schedule' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5" /> Appuntamento
            </CardTitle>
            <CardDescription>
              Fissa l'appuntamento per il passaggio consegne
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {handover.confirmed_date ? (
              <div className="p-4 bg-green-50 rounded-lg text-center">
                <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-2" />
                <p className="font-medium text-green-800">Appuntamento Confermato</p>
                <p className="text-2xl font-bold text-green-900 mt-2">
                  {formatDate(handover.confirmed_date)} alle {handover.confirmed_time}
                </p>
              </div>
            ) : (
              <>
                {/* Proposed Dates */}
                {handover.proposed_dates?.length > 0 && (
                  <div className="space-y-2">
                    <p className="font-medium">Date proposte:</p>
                    {handover.proposed_dates.map((pd, idx) => {
                      const canConfirm = pd.proposed_by !== currentUserEmail && pd.status === 'proposed';
                      return (
                        <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div>
                            <p className="font-medium">{pd.date} alle {pd.time}</p>
                            <p className="text-xs text-gray-500">
                              Proposta da {pd.proposed_by === currentUserEmail ? 'te' : 'altro responsabile'}
                            </p>
                          </div>
                          {canConfirm && (
                            <Button size="sm" onClick={() => confirmDate(idx)}>
                              <Check className="w-4 h-4 mr-1" /> Conferma
                            </Button>
                          )}
                          {pd.status === 'accepted' && (
                            <Badge className="bg-green-100 text-green-800">Confermata</Badge>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Propose New Date */}
                <div className="border-t pt-4">
                  <p className="font-medium mb-3">Proponi una data:</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm text-gray-500 block mb-1">Data</label>
                      <Input
                        type="date"
                        value={proposedDate}
                        onChange={(e) => setProposedDate(e.target.value)}
                        min={new Date().toISOString().split('T')[0]}
                      />
                    </div>
                    <div>
                      <label className="text-sm text-gray-500 block mb-1">Ora</label>
                      <Input
                        type="time"
                        value={proposedTime}
                        onChange={(e) => setProposedTime(e.target.value)}
                      />
                    </div>
                  </div>
                  <Button className="mt-4 w-full" onClick={proposeDate}>
                    <Send className="w-4 h-4 mr-2" /> Proponi data
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Verbale Section */}
      {activeSection === 'verbale' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileCheck className="w-5 h-5" /> Verbale di Passaggio
            </CardTitle>
            <CardDescription>
              Compilate insieme il verbale durante l'incontro
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {!handover.confirmed_date && (
              <div className="p-4 bg-yellow-50 rounded-lg text-center">
                <AlertCircle className="w-8 h-8 text-yellow-500 mx-auto mb-2" />
                <p className="text-yellow-800">Fissa prima l'appuntamento</p>
              </div>
            )}

            {handover.confirmed_date && !verbale && (
              <div className="text-center">
                <Button onClick={startVerbale} size="lg">
                  <PenLine className="w-5 h-5 mr-2" /> Inizia Verbale
                </Button>
              </div>
            )}

            {verbale && (
              <div className="space-y-6">
                {/* Stato Generale */}
                <div className="space-y-3">
                  <h3 className="font-medium">Stato Generale Immobile</h3>
                  <Select
                    value={verbale.general_condition || ''}
                    onValueChange={(v) => updateVerbaleField('general_condition', v)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seleziona stato" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ottimo">Ottimo</SelectItem>
                      <SelectItem value="buono">Buono</SelectItem>
                      <SelectItem value="discreto">Discreto</SelectItem>
                      <SelectItem value="scadente">Scadente</SelectItem>
                    </SelectContent>
                  </Select>
                  <Textarea
                    placeholder="Note sullo stato generale..."
                    value={verbale.general_notes || ''}
                    onChange={(e) => updateVerbaleField('general_notes', e.target.value)}
                  />
                </div>

                {/* Impianti Tecnici */}
                <div className="space-y-3">
                  <h3 className="font-medium">Impianti Tecnici</h3>
                  {['electrical', 'plumbing', 'heating', 'gas'].map(system => (
                    <div key={system} className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
                      <span className="w-32 capitalize">
                        {system === 'electrical' ? 'Elettrico' : 
                         system === 'plumbing' ? 'Idraulico' : 
                         system === 'heating' ? 'Riscaldamento' : 'Gas'}
                      </span>
                      <Select
                        value={verbale.technical_systems?.[system]?.status || ''}
                        onValueChange={(v) => updateVerbaleField(`technical_systems.${system}.status`, v)}
                      >
                        <SelectTrigger className="flex-1">
                          <SelectValue placeholder="Stato" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="funzionante">Funzionante</SelectItem>
                          <SelectItem value="parzialmente_funzionante">Parzialmente</SelectItem>
                          <SelectItem value="non_funzionante">Non funzionante</SelectItem>
                          <SelectItem value="non_presente">Non presente</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>

                {/* Chiavi */}
                <div className="space-y-3">
                  <h3 className="font-medium">Inventario Chiavi</h3>
                  {verbale.keys_inventory?.map((key, idx) => (
                    <div key={idx} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <span className="flex-1">{key.description}</span>
                      <Input
                        type="number"
                        min="0"
                        className="w-20"
                        value={key.quantity || 0}
                        onChange={(e) => {
                          const newKeys = [...(verbale.keys_inventory || [])];
                          newKeys[idx] = { ...newKeys[idx], quantity: parseInt(e.target.value) || 0 };
                          updateVerbaleField('keys_inventory', newKeys);
                        }}
                      />
                      <span className="text-sm text-gray-500">pz</span>
                    </div>
                  ))}
                </div>

                {/* Problemi */}
                <div className="space-y-3">
                  <h3 className="font-medium">Problemi e Note</h3>
                  <Textarea
                    placeholder="Problemi urgenti da segnalare..."
                    value={verbale.issues?.urgent?.join('\n') || ''}
                    onChange={(e) => updateVerbaleField('issues.urgent', e.target.value.split('\n').filter(Boolean))}
                    rows={3}
                  />
                  <Textarea
                    placeholder="Note aggiuntive..."
                    value={verbale.additional_notes || ''}
                    onChange={(e) => updateVerbaleField('additional_notes', e.target.value)}
                    rows={3}
                  />
                </div>

                {/* Save Buttons */}
                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => saveVerbale(false)} disabled={saving}>
                    <Save className="w-4 h-4 mr-2" />
                    Salva bozza
                  </Button>
                  <Button onClick={() => saveVerbale(true)} disabled={saving}>
                    <Check className="w-4 h-4 mr-2" />
                    Completa verbale
                  </Button>
                </div>

                {/* Signature Section */}
                {handover.status === 'pending_signatures' && (
                  <div className="border-t pt-6">
                    <h3 className="font-medium mb-4">Firma Digitale</h3>
                    {hasSigned ? (
                      <div className="p-4 bg-green-50 rounded-lg text-center">
                        <CheckCircle2 className="w-8 h-8 text-green-500 mx-auto mb-2" />
                        <p className="text-green-800">Hai già firmato</p>
                      </div>
                    ) : (
                      <Button className="w-full" size="lg" onClick={signHandover}>
                        <PenLine className="w-5 h-5 mr-2" />
                        Firma il verbale
                      </Button>
                    )}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Messages Section */}
      {activeSection === 'messages' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5" /> Messaggi
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Messages List */}
            <div className="max-h-96 overflow-y-auto space-y-3">
              {handover.messages?.length === 0 ? (
                <p className="text-center text-gray-500 py-8">Nessun messaggio</p>
              ) : (
                handover.messages?.map((msg, idx) => (
                  <div
                    key={idx}
                    className={`p-3 rounded-lg ${
                      msg.from === currentUserEmail 
                        ? 'bg-blue-100 ml-8' 
                        : 'bg-gray-100 mr-8'
                    }`}
                  >
                    <div className="flex justify-between items-start mb-1">
                      <span className="font-medium text-sm">{msg.from_name}</span>
                      <span className="text-xs text-gray-500">{formatDateTime(msg.sent_at)}</span>
                    </div>
                    <p className="text-sm">{msg.message}</p>
                  </div>
                ))
              )}
            </div>

            {/* Send Message */}
            <div className="flex gap-2">
              <Textarea
                placeholder="Scrivi un messaggio..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={2}
              />
              <Button onClick={sendMessage} disabled={!message.trim()}>
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default PassaggioConsegneDetail;
